import NiceModal from '@ebay/nice-modal-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useParams } from 'react-router';
import { getDeliverableChangeOrderList } from '@/api/Sherpa/ChangeOrder/index';
import { AccordionItem } from '@/components/UI/Accordion';
import { SherpaProjectReactQueryKey } from '@/constants/ReactQueryKeys';
import { TableHeader } from '@/features/ChangeOrder/Summary/TableHeader';
import type { ReactSelectOption } from '@/types/Constants';
import { FiltersModal } from '../ChangeOrderMilestones/FilterBy';
import Table from '../Table';
import { AddDeliverableModal } from './AddDeliverable';
import { columns } from './columns';

export function ChangeOrderDeliverables({
  totalImpact,
  approverOptions,
  submitterOptions,
}: {
  submitterOptions: ReactSelectOption[];
  approverOptions: ReactSelectOption[];
  totalImpact: number;
}) {
  const queryClient = useQueryClient();
  const { projectId = '' } = useParams();
  const [appliedFilters, setAppliedFilters] = useState<FieldValues>({});
  const [displayFilters, setDisplayFilters] = useState<FieldValues>({});
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 5,
  });

  const { data, isLoading } = useQuery(
    [SherpaProjectReactQueryKey.GET_CHANGE_ORDER_DELIVERABLES, { ...appliedFilters, ...pagination }],
    () =>
      getDeliverableChangeOrderList({
        ...appliedFilters,
        refType: 'project',
        refId: Number(projectId),
        page: pagination.page,
        perPage: pagination.perPage,
      })
  );

  const handleAdd = (event: React.MouseEvent) => {
    event.stopPropagation();
    NiceModal.show(AddDeliverableModal, {
      projectId,
      onSubmitted: () => {
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_CHANGE_ORDER_DELIVERABLES]);
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_CHANGE_ORDER_SUMMARY]);
      },
    });
  };

  const totalPage = Math.max(1, Math.ceil((data?.meta.total || 0) / pagination.perPage));

  return (
    <AccordionItem
      id='deliverables'
      rootClassName='bg-white rounded-md shadow-lg border-none'
      customClassName='p-0 pb-5'
      title={
        <TableHeader
          title='Deliverables'
          totalApproved={data?.meta.approved || 0}
          totalRequested={data?.meta.total || 0}
          impactValue={totalImpact}
          impactType='days'
          appliedFilters={appliedFilters}
          onFiltersOpen={(e) => {
            e.stopPropagation();
            NiceModal.show(FiltersModal, {
              submitterOptions,
              approverOptions,
              displayFilters,
              setAppliedFilters,
              setDisplayFilters,
            });
          }}
          onAdd={handleAdd}
        />
      }
    >
      <Table
        cols={columns}
        data={data?.list || []}
        fetching={isLoading}
        enableColumnResizing={false}
        wrapperClassName='!w-auto'
        tableClassName='table-fixed !w-full'
        pagination={{
          page: pagination.page,
          perPage: pagination.perPage,
          pageSize: totalPage,
          perPageHandler: (event) => {
            setPagination((prevState) => ({
              ...prevState,
              page: 1,
              perPage: Number(event.target.value),
            }));
          },
          pageChangeHandler: (direction) => {
            if (direction === 'prev') {
              setPagination((prevState) => ({
                ...prevState,
                page: Math.max(1, prevState.page - 1),
              }));
            } else {
              setPagination((prevState) => ({
                ...prevState,
                page: Math.min(prevState.page + 1, totalPage),
              }));
            }
          },
        }}
      />
    </AccordionItem>
  );
}
